// Crowdsourcing Service for State License Requirements
// Handles user submissions for missing or incorrect state data

import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient, PutCommand } from '@aws-sdk/lib-dynamodb';

const client = new DynamoDBClient({
  region: 'us-east-1',
  credentials: {
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  },
});

const docClient = DynamoDBDocumentClient.from(client);

// Environment detection for service switching
const isLocalhost = window.location.hostname === 'localhost';
const API_BASE_URL = 'https://0uh435s1v7.execute-api.us-east-1.amazonaws.com/prod';

class CrowdsourcingService {
  async submitStateRequirements(submissionData) {
    try {
      // Prepare submission with metadata (matching existing table structure)
      const submission = {
        correctionId: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        submittedDate: new Date().toISOString(),
        status: 'pending',
        state: submissionData.state,
        userEmail: submissionData.userEmail || 'anonymous',
        // Combine all details into a single field like existing structure
        details: this.formatSubmissionDetails(submissionData)
      };

      if (isLocalhost) {
        // Development: Store in DynamoDB directly
        return await this.storeToDynamoDB(submission);
      } else {
        // Production: Use API Gateway
        return await this.submitViaAPI(submission);
      }
    } catch (error) {
      console.error('Crowdsourcing submission failed:', error);
      return {
        success: false,
        error: 'Failed to submit requirements. Please try again.'
      };
    }
  }

  async storeToDynamoDB(submission) {
    try {
      const command = new PutCommand({
        TableName: 'state-licensure-corrections-dev',
        Item: submission
      });

      await docClient.send(command);

      // Trigger email notification (when SES is set up)
      await this.sendNotificationEmail(submission);

      return {
        success: true,
        correctionId: submission.correctionId,
        message: 'Thank you! Your submission has been received and will be reviewed.'
      };
    } catch (error) {
      console.error('DynamoDB storage failed:', error);
      throw error;
    }
  }

  async submitViaAPI(submission) {
    try {
      const response = await fetch(`${API_BASE_URL}/crowdsourcing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submission)
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('API submission failed:', error);
      throw error;
    }
  }

  formatSubmissionDetails(submissionData) {
    // Format all submission data into a readable details string
    let details = `Profession: ${submissionData.licenseType || 'Not specified'}\n\n`;
    
    if (submissionData.ceuRequirements) {
      details += `CEU Requirements: ${submissionData.ceuRequirements}\n\n`;
    }
    
    if (submissionData.renewalPeriod) {
      details += `Renewal Period: ${submissionData.renewalPeriod}\n\n`;
    }
    
    if (submissionData.fees) {
      details += `Fees: ${submissionData.fees}\n\n`;
    }
    
    if (submissionData.sourceUrl) {
      details += `Official Source: ${submissionData.sourceUrl}\n\n`;
    }
    
    if (submissionData.notes) {
      details += `Additional Notes: ${submissionData.notes}\n\n`;
    }
    
    details += `Submitted by: ${submissionData.userEmail || 'Anonymous'}`;
    details += `\nUser Profession: ${submissionData.profession || 'Unknown'}`;
    
    return details.trim();
  }

  async sendNotificationEmail(submission) {
    try {
      // Use existing email template system
      await this.createCrowdsourcingEmailTemplate(submission);
      
      if (isLocalhost) {
        // Development: Log email notification
        console.log('📧 Crowdsourcing Email Notification:');
        console.log('To: daniel@certifiable.app');
        console.log('Subject: 🔍 New State Requirements Correction:', submission.state);
        console.log('Correction ID:', submission.correctionId);
        console.log('Details:', submission.details);
        console.log('Email template stored in asl-ceu-tracker-email-templates-dev');
      } else {
        // Production: Use existing email service
        // TODO: Integrate with existing email sending system
        console.log('Email template created, ready for existing email service');
      }

      return { success: true };
    } catch (error) {
      console.error('Email notification failed:', error);
      // Don't fail the submission if email fails
      return { success: false, error: error.message };
    }
  }

  async createCrowdsourcingEmailTemplate(submission) {
    try {
      // Create email template in existing template system
      const emailTemplate = {
        templateId: `crowdsourcing-${submission.correctionId}`,
        templateName: `State Correction: ${submission.state}`,
        category: 'admin',
        subject: `🔍 New State Requirements Correction: ${submission.state}`,
        headerText: '🔍 New State Requirements Correction',
        bodyHtml: this.generateEmailTemplate(submission).htmlBody,
        bodyText: this.generateEmailTemplate(submission).textBody,
        isActive: true,
        sentCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'crowdsourcing-system',
        lastSentAt: null,
        // Metadata for admin processing
        correctionId: submission.correctionId,
        state: submission.state,
        userEmail: submission.userEmail
      };

      if (isLocalhost) {
        // Development: Store in email templates table
        const command = new PutCommand({
          TableName: 'asl-ceu-tracker-email-templates-dev',
          Item: emailTemplate
        });
        await docClient.send(command);
      } else {
        // Production: Use API to create template
        const response = await fetch(`${API_BASE_URL}/email-templates`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(emailTemplate)
        });
        if (!response.ok) {
          throw new Error(`Failed to create email template: ${response.status}`);
        }
      }

      return { success: true, templateId: emailTemplate.templateId };
    } catch (error) {
      console.error('Failed to create email template:', error);
      throw error;
    }
  }

  generateEmailTemplate(submission) {
    const subject = `🔍 New State Requirements Correction: ${submission.state}`;
    
    const htmlBody = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>CertifiAble - New State Requirements Submission</title>
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 20px; background-color: #f8fafc; }
        .container { max-width: 600px; margin: 0 auto; background: white; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); overflow: hidden; }
        .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px 20px; text-align: center; }
        .header h1 { margin: 0; font-size: 24px; font-weight: 600; }
        .header p { margin: 10px 0 0 0; opacity: 0.9; }
        .content { padding: 30px 20px; }
        .section { margin-bottom: 25px; }
        .section h2 { color: #4a5568; font-size: 18px; margin: 0 0 15px 0; padding-bottom: 8px; border-bottom: 2px solid #e2e8f0; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 20px; }
        .info-item { background: #f7fafc; padding: 15px; border-radius: 6px; border-left: 4px solid #667eea; }
        .info-item strong { color: #2d3748; display: block; margin-bottom: 5px; }
        .info-item span { color: #4a5568; }
        .full-width { grid-column: 1 / -1; }
        .requirements-box { background: #edf2f7; padding: 20px; border-radius: 6px; margin: 15px 0; }
        .footer { background: #f7fafc; padding: 20px; text-align: center; color: #718096; font-size: 14px; }
        .cta-button { display: inline-block; background: #667eea; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin: 15px 0; font-weight: 500; }
        .timestamp { color: #a0aec0; font-size: 12px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>🔍 New State Requirements Correction</h1>
            <p>A user has submitted a correction about state licensing requirements</p>
        </div>
        
        <div class="content">
            <div class="section">
                <h2>📍 Correction Details</h2>
                <div class="info-grid">
                    <div class="info-item">
                        <strong>State</strong>
                        <span>${submission.state}</span>
                    </div>
                    <div class="info-item">
                        <strong>Submitted By</strong>
                        <span>${submission.userEmail || 'Anonymous'}</span>
                    </div>
                    <div class="info-item full-width">
                        <strong>Correction ID</strong>
                        <span>${submission.correctionId}</span>
                    </div>
                </div>
            </div>

            <div class="section">
                <h2>📋 Correction Details</h2>
                <div class="requirements-box">
                    <pre style="white-space: pre-wrap; font-family: inherit; margin: 0;">${submission.details}</pre>
                </div>
            </div>

            <div class="section">
                <h2>⚡ Next Steps</h2>
                <p>This submission needs to be reviewed and verified before adding to the CertifiAble database:</p>
                <ol style="color: #4a5568; margin-left: 20px;">
                    <li>Verify the information against official state sources</li>
                    <li>Research specific license types and naming conventions</li>
                    <li>Update the interpreterLicensureRequirements.js file</li>
                    <li>Test the new data in the application</li>
                    <li>Mark submission as approved in the database</li>
                </ol>
            </div>
        </div>
        
        <div class="footer">
            <p class="timestamp">Submitted: ${new Date(submission.submittedDate).toLocaleString()}</p>
            <p>CertifiAble - Continuing Education Tracking for ASL Interpreters</p>
        </div>
    </div>
</body>
</html>`;

    const textBody = `
🔍 NEW STATE REQUIREMENTS SUBMISSION

State: ${submission.state}
Profession Type: ${submission.licenseType}
Submitted By: ${submission.userEmail || 'Anonymous'}
User Profession: ${submission.profession || 'Unknown'}
Submission ID: ${submission.submissionId}

REQUIREMENTS INFORMATION:
CEU Requirements: ${submission.ceuRequirements || 'Not provided'}
Renewal Period: ${submission.renewalPeriod || 'Not provided'}
Fees: ${submission.fees || 'Not provided'}
Official Source URL: ${submission.sourceUrl || 'Not provided'}
${submission.notes ? `Additional Notes: ${submission.notes}` : ''}

NEXT STEPS:
1. Verify information against official state sources
2. Research specific license types and naming conventions  
3. Update interpreterLicensureRequirements.js file
4. Test new data in application
5. Mark submission as approved

Submitted: ${new Date(submission.timestamp).toLocaleString()}
CertifiAble - Continuing Education Tracking for ASL Interpreters
`;

    return {
      subject,
      htmlBody,
      textBody,
      to: 'daniel@certifiable.app',
      from: 'noreply@certifiable.app'
    };
  }

  // Admin functions for managing submissions
  async getSubmissions(status = 'pending') {
    try {
      if (isLocalhost) {
        // Development: Query DynamoDB directly
        // TODO: Implement scan with filter
        console.log('Getting submissions from DynamoDB');
        return { success: true, submissions: [] };
      } else {
        // Production: Use API Gateway
        const response = await fetch(`${API_BASE_URL}/crowdsourcing?status=${status}`);
        return await response.json();
      }
    } catch (error) {
      console.error('Failed to get submissions:', error);
      return { success: false, error: error.message };
    }
  }

  async approveSubmission(correctionId, reviewerEmail) {
    try {
      const updateData = {
        status: 'approved',
        reviewedBy: reviewerEmail,
        reviewedAt: new Date().toISOString()
      };

      if (isLocalhost) {
        // Development: Update DynamoDB directly
        console.log('Approving correction:', correctionId, updateData);
        return { success: true };
      } else {
        // Production: Use API Gateway
        const response = await fetch(`${API_BASE_URL}/crowdsourcing/${correctionId}/approve`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updateData)
        });
        return await response.json();
      }
    } catch (error) {
      console.error('Failed to approve submission:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new CrowdsourcingService();